import { useMemo } from 'react';
import { useTranslation } from '@/components/utils/translations';
import guideEn from '@/content/help/guide.html?raw';
import guideEs from '@/content/help/guide.es.html?raw';
import guidePtBr from '@/content/help/guide.pt-BR.html?raw';
import guideFr from '@/content/help/guide.fr.html?raw';

// One copy of the guide per Settings → Language code; English is the default and the fallback.
const GUIDES = { en: guideEn, es: guideEs, 'pt-BR': guidePtBr, fr: guideFr };

// Screenshots are bundled with the app (not served from public/), so the guide is reachable only through
// this signed-in page. Vite resolves each to its hashed asset URL.
const SHOTS = import.meta.glob('@/content/help/*.jpg', { eager: true, query: '?url', import: 'default' });
const shotUrl = Object.fromEntries(Object.entries(SHOTS).map(([path, url]) => [path.split('/').pop(), url]));

// In-page links (#market, #faq …) scroll inside the frame instead of navigating it.
const ANCHOR_SCRIPT = `<script>
document.addEventListener('click', function (e) {
  var a = e.target.closest('a[href^="#"]');
  if (!a) return;
  var el = document.getElementById(a.getAttribute('href').slice(1));
  if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
});
</script>`;

/**
 * Help: the "Getting started with NEO" guide (screenshots use sample names), shown inside the app layout
 * so the top nav stays as it is. Signed-out visitors get the login page, like every non-public route.
 */
export default function Help() {
  const { language } = useTranslation();
  const guideHtml = GUIDES[language] || GUIDES.en;
  const srcDoc = useMemo(
    () => guideHtml
      .replace(/src="([\w.-]+\.jpg)"/g, (m, file) => (shotUrl[file] ? `src="${shotUrl[file]}"` : m))
      .replace('</body>', `${ANCHOR_SCRIPT}</body>`),
    [guideHtml]
  );
  return (
    <iframe
      key={language}
      srcDoc={srcDoc}
      title={guideHtml.match(/<title>([^<]*)<\/title>/)?.[1] || 'Getting started with NEO'}
      className="w-full border-0 bg-transparent"
      style={{ height: 'calc(100vh - 64px - 4rem)' }}
    />
  );
}
